import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Inicializar el estado de autenticación basándose en el token existente
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token');
    return !!token; // Convierte el token a boolean
  });
  
  const [user, setUser] = useState(() => { // Inicializar el estado del usuario
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        return JSON.parse(userData);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  
  const [permissions, setPermissions] = useState(() => { // Inicializar el estado de permisos
    const permissionsData = localStorage.getItem('permissions');
    if (permissionsData) {
      try {
        return JSON.parse(permissionsData);
      } catch (e) {
        return {};
      }
    }
    return {};
  });

  // Verificar la validez del token al cargar la aplicación
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Aquí podrías hacer una llamada al backend para verificar el token
      // Por ahora, simplemente asumimos que el token es válido si existe
      // En una implementación real, deberías verificar con el backend
      console.log('Token encontrado, usuario autenticado');
    }
  }, []);

  // Funciones helper para verificar permisos
  const hasPermission = (permissionKey, requiredLevel = 'READ') => {
    const userPermission = permissions[permissionKey];
    
    if (!userPermission) return false;
    
    switch (requiredLevel) {
      case 'WRITE':
        return userPermission === 'WRITE';
      case 'READ':
        return userPermission === 'READ' || userPermission === 'WRITE';
      case 'NO_ACCESS':
        return userPermission === 'NO_ACCESS';
      default:
        return false;
    }
  };

  const canRead = (permissionKey) => hasPermission(permissionKey, 'READ');
  const canWrite = (permissionKey) => hasPermission(permissionKey, 'WRITE');
  const hasNoAccess = (permissionKey) => hasPermission(permissionKey, 'NO_ACCESS');

  // Función para obtener el nivel de permiso como string
  const getPermissionLevel = (permissionKey) => {
    return permissions[permissionKey] || 'NO_ACCESS';
  };

  // Función para mostrar mensaje informativo sobre permisos
  const getPermissionMessage = (permissionKey) => {
    const level = getPermissionLevel(permissionKey);
    switch (level) {
      case 'WRITE':
        return 'Tienes permisos completos (lectura y escritura)';
      case 'READ':
        return 'Tienes permisos de solo lectura';
      case 'NO_ACCESS':
        return 'No tienes acceso a esta sección';
      default:
        return 'Permisos no definidos';
    }
  };

  const login = (user, permissions) => {
    setIsAuthenticated(true);
    setUser(user);
    setPermissions(permissions);
    
    // Guardar datos en localStorage para persistencia
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('permissions', JSON.stringify(permissions));
    
    // Mostrar permisos en consola para debugging
    console.log('🔐 Usuario logueado:', user);
    console.log('🔐 Permisos asignados:', permissions);
  };
  
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setPermissions({});
    
    // Limpiar localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    
    console.log('🔐 Usuario deslogueado');
  };

  return ( // Proveer el contexto
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      permissions, 
      login, 
      logout, 
      user,
      hasPermission,
      canRead,
      canWrite,
      hasNoAccess,
      getPermissionLevel,
      getPermissionMessage
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);